import { Component, EventEmitter, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { Subscription } from 'rxjs/internal/Subscription';
import { GroupMemberInterface } from 'src/app/interfaces/groups/group.interface';
import { notEmpty } from '../../modals/groups-details/groups-details.component';
import { BaseGroupForm } from '../marketing-campaign-group-base-form/marketing-campaign-group-base-form';

export function unique(form: FormGroup): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    const emailsAsFormArray = form.get('emailAddresses') as FormArray;
    const index = emailsAsFormArray.controls.findIndex((ctrl) => ctrl.value === value);

    return index === -1 ? null : { notUnique: true };
  };
}

@Component({
  selector: 'app-marketing-campaign-group-email-form',
  templateUrl: './marketing-campaign-group-email-form.component.html',
  styleUrls: ['./marketing-campaign-group-email-form.component.scss'],
})
export class MarketingCampaignGroupEmailFormComponent
  extends BaseGroupForm
  implements OnChanges, OnInit
{
  @Output() emailRemoved = new EventEmitter<string>();

  protected errorMessage: string | null = null;

  private valueChangesDisposable: Subscription | undefined;
  protected currentEmailForm: FormControl = new FormControl<string>('');

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.currentEmailForm.valueChanges.subscribe((value) => {
      if (value.length >= 1) {
        if (!this.currentEmailForm.hasValidator(Validators.email)) {
          this.currentEmailForm.setValidators([
            Validators.required,
            Validators.email,
            unique(this.form as FormGroup),
          ]);
          this.currentEmailForm.updateValueAndValidity();
        }
      }
    });
  }

  addEmailToList(): void {
    if (!this.currentEmailForm.hasValidator(Validators.email)) {
      this.currentEmailForm.setValidators([
        Validators.required,
        Validators.email,
        unique(this.form as FormGroup),
      ]);
      this.currentEmailForm.updateValueAndValidity();
    }
    if (this.currentEmailForm.invalid) {
      if (this.currentEmailForm.hasError('notUnique')) {
        this.errorMessage = 'email is already on the list';
      }
      return;
    }
    const email = this.currentEmailForm.value;
    const emailsAsFormArray = this.form?.get('emailAddresses') as FormArray;
    emailsAsFormArray.push(new FormControl(email));
    this.currentEmailForm.setValue('', { onlySelf: true });
    this.currentEmailForm.clearValidators();
    this.currentEmailForm.updateValueAndValidity();
  }

  getEmailsAsFormArray(): FormArray {
    return this.form?.controls.emailAddresses as FormArray;
  }

  remove(i: number): void {
    const emailsAsFormArray = this.form?.get('emailAddresses') as FormArray;
    emailsAsFormArray.removeAt(i);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // TODO: this is strange, but component behavior demands somethign like this
    this.valueChangesDisposable?.unsubscribe();
    if (changes.form && changes.form.currentValue) {
      this.currentEmailForm.setValidators([
        Validators.required,
        Validators.email,
        unique(this.form as FormGroup),
      ]);
      this.currentEmailForm.updateValueAndValidity();

      this.valueChangesDisposable = this.form?.valueChanges.subscribe((_) => {
        if (
          this.form?.get('emailAddresses')?.touched &&
          this.form?.get('emailAddresses')?.invalid
        ) {
          if (this.form?.get('emailAddresses')?.hasError('notEmpty')) {
            this.errorMessage = 'add at least one email!';
          } else {
            this.errorMessage = 'emails is invalid';
          }
        } else if (this.form?.get('name')?.touched && this.form?.get('name')?.invalid) {
          if (this.form?.get('name')?.hasError('required')) {
            this.errorMessage = 'name is required';
          } else {
            this.errorMessage = 'name is invalid';
          }
        } else {
          this.errorMessage = null;
        }
      });
    }
  }

  protected getDiffIndices(
    prev: any[],
    curr: any[],
  ): { value: boolean; idx?: number; email?: string } | null {
    if (prev.length > curr.length) {
      const email = prev.filter((item) => !curr.includes(item))[0];
      return { email, value: false };
    } else {
      const email = curr.filter((item) => !prev.includes(item))[0];
      return { email, value: true };
    }
  }

  protected getMemberInfo(diff: { value: boolean; idx?: number; email?: string }): {
    id: string;
    name: string;
  } {
    if (diff.email) {
      return { id: diff.email, name: diff.email };
    }

    throw new Error('Invalid diff object');
  }

  async addFormItemsControl(): Promise<void> {
    this.form.removeControl('items');

    const formArray = this.fb.array(
      (this.data ? this.data.members : []).map(
        (email: string) => new FormControl(email),
      ),
      notEmpty(),
    );

    this.form.addControl('emailAddresses', formArray);
  }

  protected async handleGroupInterfaceChange(): Promise<void> {
    await this.addFormItemsControl();
  }

  protected registerFormListeners(): void {
    this.form.valueChanges.subscribe(() => {
      const rawData = this.form.getRawValue();
      this.canSave = this.form.valid;
      this.formSnapshots.set(this.groupInterface, rawData);
    });
  }

  protected revertFormState(previousValues: any[]): void {
    const emailArray = this.form.get('emailAddresses') as FormArray;
    emailArray.clear({ emitEvent: false });

    previousValues.forEach((email) => {
      emailArray.push(new FormControl(email), { emitEvent: false });
    });

    emailArray.updateValueAndValidity({ emitEvent: false });

    if (this.subscription) this.subscription.unsubscribe();
    this.trackAndUpdate();
  }

  protected async save(): Promise<void> {
    try {
      const formData = this.formSnapshots.get(GroupMemberInterface.EmailAddress);

      const payload = {
        name: formData.name,
        description: formData.description,
      };

      await this.groupsService.createGroup(
        { ...payload, emailAddresses: formData.emailAddresses },
        'emailAddress',
      );

      this.saved.emit({ data: formData });
    } catch (error) {
      console.error(error);
      this.snackBar.open('Error while creating a Group. Try again in a few minutes.');
    }
  }
}
